// components/steps/Step1BasicInfo.tsx - Basic Information Step Component
// Updated: Split into read-only identity section and editable request details section

import React from 'react';
import { Step1Props } from '../../types/GatePassTypes';
import { REQUEST_TYPES, PASS_TYPES } from '../../constants/GatePassConstants';

export const Step1BasicInfo: React.FC<Step1Props> = ({
  formData,
  regions,
  loadingRegions,
  regionError,
  onChange,
  onRefreshRegions,
  selectedParentRequest,
  onOpenParentRequestPopup,
  onClearParentRequest
}) => {

  const isSupplementary = formData.requestType === 'supplementary';

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="step-content">

      {/* ─── Requester Information — read-only fields in form grid ───── */}
      <div className="step1-form-grid" style={{ marginBottom: '8px' }}>
        <div style={readonlyFieldStyle}>
          <span style={readonlyLabelStyle}>Requester Name:</span>
          <span style={readonlyValueStyle}>{formData.name || '—'}</span>
        </div>
        <div style={readonlyFieldStyle}>
          <span style={readonlyLabelStyle}>Email:</span>
          <span style={readonlyValueStyle}>{formData.email || '—'}</span>
        </div>
        <div style={readonlyFieldStyle}>
          <span style={readonlyLabelStyle}>Company:</span>
          <span style={readonlyValueStyle}>{formData.companyName || '—'}</span>
        </div>
      </div>

      {/* ─── Request Details ─────────────────────────────────────────── */}
      <div style={{ marginBottom: '0' }}>

        {/* Subheading: Request Information */}
        <div style={sectionHeadingStyle}>Request Information</div>

        <div className="step1-form-grid">

          {/* Region */}
          <div className="form-field">
            <label htmlFor="regionId" className="field-label">
              Region <span className="required">*</span>
            </label>
            {loadingRegions ? (
              <div className="loading-placeholder">Loading regions...</div>
            ) : (
              <select
                id="regionId"
                name="regionId"
                value={formData.regionId || ''}
                onChange={onChange}
                required
                className="field-select"
              >
                <option value="">Please select</option>
                {regions.map((region) => (
                  <option key={region.regionId} value={region.regionId}>
                    {region.regionName} ({region.regionCode})
                  </option>
                ))}
              </select>
            )}
            {regionError && (
              <div className="field-error">
                {regionError}
                <button type="button" onClick={onRefreshRegions} className="retry-btn">Retry</button>
              </div>
            )}
          </div>

          {/* Request Type */}
          <div className="form-field">
            <label htmlFor="requestType" className="field-label">
              Request Type <span className="required">*</span>
            </label>
            <select
              id="requestType"
              name="requestType"
              value={formData.requestType}
              onChange={onChange}
              required
              className="field-select"
            >
              {REQUEST_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* ─── Parent Request Selection (Supplementary only) ────────── */}
          {isSupplementary && (
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label className="field-label">
                Parent Request <span className="required">*</span>
              </label>

              {selectedParentRequest ? (
                <div style={parentRequestCardStyle}>
                  <div style={parentRequestCardHeaderStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={parentIdBadgeStyle}>#{selectedParentRequest.id}</span>
                      <span style={{ color: '#374151', fontSize: '14px', fontWeight: 600 }}>{selectedParentRequest.name}</span>
                      <span style={parentStatusBadgeStyle}>Approved</span>
                    </div>
                    <button type="button" onClick={onClearParentRequest} style={changeButtonStyle}>Change</button>
                  </div>
                  <div style={parentRequestDetailsStyle}>
                    <div style={detailRowStyle}>
                      <span style={detailLabelStyle}>Company</span>
                      <span style={detailValueStyle}>{selectedParentRequest.companyName}</span>
                    </div>
                    {selectedParentRequest.contractNumber && (
                      <div style={detailRowStyle}>
                        <span style={detailLabelStyle}>Contract</span>
                        <span style={detailValueStyle}>{selectedParentRequest.contractNumber}</span>
                      </div>
                    )}
                    {selectedParentRequest.startDate && selectedParentRequest.endDate && (
                      <div style={detailRowStyle}>
                        <span style={detailLabelStyle}>Validity Period</span>
                        <span style={detailValueStyle}>
                          {formatDate(selectedParentRequest.startDate)} → {formatDate(selectedParentRequest.endDate)}
                        </span>
                      </div>
                    )}
                    <div style={detailRowStyle}>
                      <span style={detailLabelStyle}>Submitted</span>
                      <span style={detailValueStyle}>{formatDate(selectedParentRequest.submittedAt)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={onOpenParentRequestPopup} style={selectParentButtonStyle}>
                  <span style={{ fontSize: '20px' }}>📋</span>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '14px' }}>Select Parent Request</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Choose from your approved requests</div>
                  </div>
                </button>
              )}

              {isSupplementary && !selectedParentRequest && (
                <span className="field-error" style={{ marginTop: '6px', display: 'block' }}>
                  A parent request must be selected for supplementary requests
                </span>
              )}
            </div>
          )}

          {/* ── Subheading: Pass Details ── */}
          <div className="form-field full-width section-heading-field">
            <div style={sectionHeadingStyle}>Pass Details</div>
          </div>

          {/* Pass Type */}
          <div className="form-field">
            <label htmlFor="passType" className="field-label">
              Pass Type <span className="required">*</span>
            </label>
            <select
              id="passType"
              name="passType"
              value={formData.passType || ''}
              onChange={onChange}
              required
              className="field-select"
            >
              <option value="" disabled>Please select</option>
              {PASS_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Contract Number */}
          <div className="form-field">
            <label htmlFor="contactNumber" className="field-label">
              Contract Number <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="contactNumber"
              name="contractNumber"
              value={formData.contractNumber || ''}
              onChange={onChange}
              required
              placeholder="e.g. 123456789"
              className="field-input"
            />
          </div>

          {/* Start Date */}
          <div className="form-field">
            <label htmlFor="startDate" className="field-label">
              Start Date <span className="required">*</span>
            </label>
            <div style={{ position: 'relative', width: '100%', height: '28px', cursor: 'pointer' }}>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate || ''}
                onChange={onChange}
                required
                style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                  opacity: 0, cursor: 'pointer', zIndex: 2, margin: 0, padding: 0,
                  border: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%', pointerEvents: 'none' }}>
                <span style={{ fontSize: '14px', fontWeight: 400, color: '#1f2937', lineHeight: 1.5, fontFamily: 'inherit' }}>
                  {formData.startDate ? formatDate(formData.startDate) : ''}
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
            </div>
          </div>

          {/* End Date */}
          <div className="form-field">
            <label htmlFor="endDate" className="field-label">
              End Date <span className="required">*</span>
            </label>
            <div style={{ position: 'relative', width: '100%', height: '28px', cursor: 'pointer' }}>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate || ''}
                onChange={onChange}
                required
                min={formData.startDate || undefined}
                style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                  opacity: 0, cursor: 'pointer', zIndex: 2, margin: 0, padding: 0,
                  border: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%', pointerEvents: 'none' }}>
                <span style={{ fontSize: '14px', fontWeight: 400, color: '#1f2937', lineHeight: 1.5, fontFamily: 'inherit' }}>
                  {formData.endDate ? formatDate(formData.endDate) : ''}
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
            </div>
          </div>

          {/* Pass Identification Name */}
          <div className="form-field">
            <label htmlFor="passIdentificationName" className="field-label">
              Pass Identification Name
            </label>
            <input
              type="text"
              id="passIdentificationName"
              name="passIdentificationName"
              value={formData.passIdentificationName}
              onChange={onChange}
              placeholder="e.g. Name_1"
              className="field-input"
            />
          </div>

        </div>
      </div>
    </div>
  );
};



const sectionHeadingStyle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 700,
  color: '#1a1a1a',
  marginBottom: '2px',
  marginTop: '6px',
  lineHeight: 1.3,
};

// Read-only field container — no underline
const readonlyFieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  boxSizing: 'border-box',
  padding: '14px 0 12px',
  margin: 0,
};

// Read-only label — bold, like reference design
const readonlyLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: 700,
  color: '#1a1a1a',
  marginBottom: '4px',
  lineHeight: 1.3,
};

// Read-only value
const readonlyValueStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 400,
  color: '#374151',
  lineHeight: 1.5,
  padding: '2px 0 4px',
};

// ─── Inline Styles for Parent Request Selection ───────────────────────

const parentRequestCardStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: '4px',
  backgroundColor: '#ffffff',
  overflow: 'hidden',
  boxShadow: '0 1px 3px 0 rgba(0,0,0,0.08)'
};

const parentRequestCardHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  backgroundColor: '#FEF6F4',
  borderBottom: '1px solid #f1f5f9'
};

const parentIdBadgeStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#F3694A'
};

const parentStatusBadgeStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#059669',
  backgroundColor: '#ecfdf5',
  padding: '2px 8px',
  borderRadius: '4px',
  letterSpacing: '0.02em'
};

const changeButtonStyle: React.CSSProperties = {
  padding: '5px 12px',
  backgroundColor: '#ffffff',
  color: '#F3694A',
  border: '1px solid #F3694A',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 600,
  fontFamily: "'Segoe UI', sans-serif",
  transition: 'all 0.15s ease'
};

const parentRequestDetailsStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0',
  padding: '0'
};

const detailRowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  padding: '10px 16px',
  borderBottom: '1px solid #f8fafc'
};

const detailLabelStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#6b7280',
  fontWeight: 500
};

const detailValueStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#1e293b',
  fontWeight: 500
};

const selectParentButtonStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '16px 20px',
  border: '1.5px dashed #d1d5db',
  borderRadius: '4px',
  backgroundColor: '#fafbfc',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'all 0.15s ease'
};